import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateTransition } from '../lib/navigation';
import IDCard from '../components/IDCard';
import { useFlow } from '../state/flow';
import { composeShare, type ShareSize } from '../lib/composeShare';
import { rasterizeLockup } from '../lib/photo';
import { shareOrDownload } from '../lib/shareOrDownload';
import { trackEvent } from '../lib/analytics';
import DownloadIcon from '../components/DownloadIcon';
import './Share.scss';

/** Right-pane content for /share — the two share-asset tiles. Layered against
 * WelcomeRight inside WelcomeShareFlow so only this region fades on route. */
export function ShareRight() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigateTransition();
  const { photoDataUrl, shareImages, setShareImages } = useFlow();
  const locale = i18n.resolvedLanguage ?? 'en';
  const hiddenCardRef = useRef<HTMLDivElement | null>(null);
  const [previews, setPreviews] = useState<Record<ShareSize, string | null>>(
    shareImages ?? { '3x4': null, '9x16': null },
  );
  const [busy, setBusy] = useState<ShareSize | null>(null);

  const renderLockup = useCallback(async (): Promise<string | null> => {
    if (!hiddenCardRef.current) return null;
    return rasterizeLockup(hiddenCardRef.current);
  }, []);

  // Fallback compose path — only runs if /loading didn't manage to bake the
  // previews (slow device, page reloaded into /share, etc.).
  useEffect(() => {
    if (previews['3x4'] && previews['9x16']) return;
    let alive = true;
    (async () => {
      const lockup = await renderLockup();
      if (!lockup) {
        navigate('/');
        return;
      }
      const [a, b] = await Promise.all([
        composeShare('3x4', lockup, locale, photoDataUrl),
        composeShare('9x16', lockup, locale, photoDataUrl),
      ]);
      if (!alive) return;
      const next = { '3x4': a, '9x16': b };
      setPreviews(next);
      setShareImages(next);
    })();
    return () => {
      alive = false;
    };
  }, [renderLockup, navigate, previews, setShareImages, locale, photoDataUrl]);

  async function handleShare(size: ShareSize) {
    if (busy) return;
    setBusy(size);
    try {
      const png =
        previews[size] ??
        (await composeShare(size, (await renderLockup()) ?? '', locale, photoDataUrl));
      await shareOrDownload(png, `briar-u-share-${size}.png`);
      trackEvent('share_downloaded', { size });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="share-tiles">
        <ShareTile
          label={t('share.grid')}
          downloadLabel={t('common.download')}
          previewUrl={previews['3x4']}
          busy={busy === '3x4'}
          onAction={() => handleShare('3x4')}
          aspectRatio="3 / 4"
        />
        <ShareTile
          label={t('share.story')}
          downloadLabel={t('common.download')}
          previewUrl={previews['9x16']}
          busy={busy === '9x16'}
          onAction={() => handleShare('9x16')}
          aspectRatio="9 / 16"
        />
      </div>
      <div className="share-offscreen" aria-hidden="true">
        <IDCard ref={hiddenCardRef} />
      </div>
    </>
  );
}

function ShareTile({
  label,
  downloadLabel,
  previewUrl,
  busy,
  onAction,
  aspectRatio,
}: {
  label: string;
  downloadLabel: string;
  previewUrl: string | null;
  busy: boolean;
  onAction: () => void;
  aspectRatio: string;
}) {
  return (
    <button
      type="button"
      className="share-tile"
      onClick={onAction}
      disabled={!previewUrl || busy}
      aria-busy={busy}
      aria-label={label}
    >
      <div className="share-tile__preview" style={{ aspectRatio }}>
        {previewUrl ? <img src={previewUrl} alt="" /> : <div className="share-tile__skeleton" />}
      </div>
      <span className="share-tile__label share-tile__label--mobile">{label}</span>
      <span className="share-tile__label share-tile__label--desktop">
        {downloadLabel} <DownloadIcon />
      </span>
    </button>
  );
}
